const API_ROOT = '/api/v1';
const CSRF_COOKIE = 'auth_csrf';

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
  }
}

/**
 * O token vem sempre do cookie (não é httpOnly, precisamente para o padrão double-submit).
 * Guardá-lo em memória dessincroniza-o: o login e o `/auth/csrf` rodam o cookie do lado do
 * servidor, e a cópia antiga faria a mutação seguinte falhar com 403.
 */
function csrfFromCookie(): string | undefined {
  const entry = document.cookie.split('; ').find((cookie) => cookie.startsWith(`${CSRF_COOKIE}=`));
  return entry ? decodeURIComponent(entry.slice(CSRF_COOKIE.length + 1)) : undefined;
}

async function refreshCsrf(): Promise<string> {
  const response = await fetch(`${API_ROOT}/auth/csrf`, { credentials: 'include' });
  if (!response.ok) throw new ApiError(response.status, 'CSRF_FAILED', 'Não foi possível iniciar uma sessão segura');
  const data = await response.json() as { csrfToken: string };
  return data.csrfToken;
}

async function request<T>(path: string, options: RequestInit = {}, retry = true): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const mutating = !['GET', 'HEAD', 'OPTIONS'].includes(method);
  const csrfToken = mutating ? csrfFromCookie() ?? await refreshCsrf() : undefined;
  const response = await fetch(`${API_ROOT}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
      ...options.headers,
    },
  });
  if (response.status === 403 && mutating && retry) {
    await refreshCsrf();
    return request<T>(path, options, false);
  }
  if (!response.ok) {
    const payload = await response.json().catch(() => ({ error: { code: 'HTTP_ERROR', message: response.statusText } })) as {
      error?: { code?: string; message?: string; details?: unknown };
    };
    throw new ApiError(response.status, payload.error?.code ?? 'HTTP_ERROR', payload.error?.message ?? 'O pedido falhou', payload.error?.details);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', ...(body !== undefined ? { body: JSON.stringify(body) } : {}) }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', ...(body !== undefined ? { body: JSON.stringify(body) } : {}) }),
};
